import { apiClient, getData } from '@/api/client'
import { hasCode } from '@/api/errors'
import type { components } from '@/api/schema'
import { blankToNull, knownWord } from '@/schemas/common'
import {
  INSIGHT_TONES,
  INSIGHT_UNAVAILABLE_REASONS,
  INSIGHT_UPDATING_REASONS,
  companyInsightSchema,
  type CompanyInsight,
  type InsightPage,
  type InsightVote,
} from '@/schemas/company-insights'

/**
 * Mitra's take on the Stock page: `/api/v5/company/{symbol}/insights/…` in,
 * app types out.
 *
 * Structured like `profileService.ts` — wire types from `@/api/schema`
 * through `Wire<>`, every mapper's result through its Zod schema — but its
 * own module: that file is the tabs' figures and only reads, and this is
 * mm-be's `services/insights`, with a write beside the read and a schema file
 * of its own (`@/schemas/company-insights`).
 *
 * ENGLISH ONLY. Every call names `lang: 'en'`. Without it mm-be falls back to
 * the reader's saved language preference, so a member who picked Nepali in
 * the mobile app would get Nepali takes inside an English page — and their
 * votes would be filed against the Nepali text they never saw.
 *
 * `symbol` is the page's upper-cased ticker, as StockScreen holds it, and is
 * not normalised again here (see `profileService.ts`). Every call is
 * JWT-only (mm-be's `V5APIView`).
 */

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

/** The one language the web app reads and votes in. */
const LANGUAGE = 'en'

/**
 * Null when the headline or the summary is blank: a card with nothing to say
 * is no card, and the take then reads as unavailable rather than failing.
 */
const mapInsightCard = (wire: Wire<'Card'>) => {
  const headline = blankToNull(wire.headline)
  const summary = blankToNull(wire.summary)
  if (headline === null || summary === null) return null
  return {
    headline: headline.trim(),
    summary: summary.trim(),
    tone: knownWord(INSIGHT_TONES, wire.tone),
    featured: wire.featured.filter((id) => id.trim() !== ''),
  }
}

/**
 * Trimmed, or null when blank. Null-safe although the spec says string: DRF
 * serialises a stored null as null, whatever the field declares.
 */
/**
 * Mitra's brief draws a bar chart under each section in the mock; mm-be's explainer carries no
 * series yet (`asks/cp1.md` A9). Off, every section's `chart` is null and the popup says "not live
 * yet" in the chart's place; one constant to flip once `sections[].chart` ships.
 */
export const SECTION_CHARTS_AVAILABLE = false as boolean

const trimmed = (value: string | null): string | null => blankToNull(value)?.trim() ?? null

/**
 * Null only when nothing usable is left: no title, no lede, no section, no
 * takeaway, no glossary word and no keep-in-mind line. A blank title alone
 * keeps the explainer (mm-be's scrub blanks a title that quotes an untraced
 * number, and serves the rest); the popup then falls back to the card's
 * headline. A blank takeaway, and a section or glossary row with a blank
 * half, is left for `insightExplainerSchema`'s `lenientList` to drop, and
 * counts as nothing here.
 */
const mapInsightExplainer = (wire: Wire<'Explainer'>) => {
  const title = trimmed(wire.title)
  const oneLiner = trimmed(wire.one_liner)
  const keepInMind = trimmed(wire.keep_in_mind)
  const usable =
    title !== null ||
    oneLiner !== null ||
    keepInMind !== null ||
    wire.sections.some((s) => trimmed(s.heading) !== null && trimmed(s.body) !== null) ||
    wire.takeaways.some((takeaway) => trimmed(takeaway) !== null) ||
    wire.glossary.some((g) => trimmed(g.term) !== null && trimmed(g.plain) !== null)
  if (!usable) return null
  return {
    title,
    oneLiner,
    sections: wire.sections.map((section) => ({
      heading: trimmed(section.heading),
      body: trimmed(section.body),
      keyNumber: blankToNull(section.key_number),
      // Proposed field (asks/cp1.md A9), not in the generated wire type yet.
      chart: SECTION_CHARTS_AVAILABLE ? ((section as { chart?: unknown }).chart ?? null) : null,
    })),
    takeaways: wire.takeaways.map((takeaway) => trimmed(takeaway)),
    glossary: wire.glossary.map((item) => ({
      term: trimmed(item.term),
      plain: trimmed(item.plain),
    })),
    keepInMind,
  }
}

const mapInsightKeyNumber = (wire: Wire<'KeyNumber'>) => ({
  id: wire.id,
  label: blankToNull(wire.label) ?? wire.id,
  display: wire.display,
  unit: wire.unit,
  context: blankToNull(wire.context),
  asDate: wire.as_date,
})

/**
 * One take, narrowed to the three states `companyInsightSchema` allows.
 * Exported so it is testable on a fixture without a request.
 *
 * `ready` needs a card that maps; a `ready` without one, and any status this
 * build does not know, become `unavailable` — never an error. A take that is
 * not ready keeps no card, no key numbers and no explainer, whatever the wire
 * carries: mm-be sends none today, and the rule is that nothing out of date is
 * ever drawn.
 *
 * `page` is the page ASKED for, so the result can never name a different
 * one from its cache key.
 */
export const mapInsight = (wire: Wire<'Insight'>, page: InsightPage) => {
  const base = {
    symbol: wire.symbol,
    page,
    asOf: wire.as_of,
    updatedAt: wire.updated_at,
    disclaimer: wire.disclaimer,
  }
  const card = wire.card === null ? null : mapInsightCard(wire.card)
  if (wire.status === 'ready' && card !== null) {
    return {
      ...base,
      status: 'ready',
      reason: null,
      card,
      keyNumbers: wire.key_numbers.map(mapInsightKeyNumber),
      explainer: wire.explainer === null ? null : mapInsightExplainer(wire.explainer),
    }
  }
  if (wire.status === 'updating') {
    return {
      ...base,
      status: 'updating',
      reason: knownWord(INSIGHT_UPDATING_REASONS, wire.reason),
      card: null,
      keyNumbers: [],
      explainer: null,
    }
  }
  return {
    ...base,
    status: 'unavailable',
    reason: knownWord(INSIGHT_UNAVAILABLE_REASONS, wire.reason),
    card: null,
    keyNumbers: [],
    explainer: null,
  }
}

/**
 * `card` is the take card alone; `full` adds the explainer, for the "Mitra
 * explains" popup. mm-be's own default is `full`, so the card read names it.
 */
type InsightPart = 'card' | 'full'

/**
 * Resolves `null` on the two 404s that mean "this company has no takes" —
 * `INSIGHTS_COMPANY_NOT_COVERED` (a fund, a debenture, an index) and
 * `COMPANY_NOT_FOUND` — so the screen hides the take. Both are permanent
 * answers, safe to cache as a value (docs/route-loading.md, "Not-found
 * answers"). Every other failure throws, so a 5xx keeps the app's retry
 * (`app/query-client.ts`) and is never cached as data.
 */
const readInsight = async (
  symbol: string,
  page: InsightPage,
  part: InsightPart,
): Promise<CompanyInsight | null> => {
  try {
    const data = await getData<Wire<'Insight'>>(() =>
      apiClient.GET('/api/v5/company/{symbol}/insights/{page}/', {
        params: { path: { symbol, page }, query: { part, lang: LANGUAGE } },
      }),
    )
    // A card read holds no explainer even if mm-be ever sent one, so the
    // card's cache entry never carries text the popup's read is keyed for.
    const wire = part === 'card' ? { ...data, explainer: null } : data
    return companyInsightSchema.parse(mapInsight(wire, page))
  } catch (error) {
    if (hasCode(error, 'INSIGHTS_COMPANY_NOT_COVERED') || hasCode(error, 'COMPANY_NOT_FOUND')) {
      return null
    }
    throw error
  }
}

export const companyInsightsService = {
  /** One page's take card (`part=card`): its `explainer` is always null. */
  insight: (symbol: string, page: InsightPage): Promise<CompanyInsight | null> =>
    readInsight(symbol, page, 'card'),

  /**
   * The same take with its explainer (`part=full`), read when the "Mitra
   * explains" popup opens. The same states and the same `null` as `insight`.
   */
  insightFull: (symbol: string, page: InsightPage): Promise<CompanyInsight | null> =>
    readInsight(symbol, page, 'full'),

  /**
   * 👍/👎 on one take. Throws `409 INSIGHTS_NOT_READY` when the take is not
   * ready any more and `429 RATE_LIMITED` past mm-be's vote budget — read
   * both with `insightFeedbackFailure` (`./errors.ts`).
   *
   * `comment` is the wire's own default, sent empty because the generated
   * type requires it: the web app collects no comment.
   */
  insightFeedback: async (symbol: string, page: InsightPage, vote: InsightVote): Promise<void> => {
    await getData<Wire<'FeedbackReceipt'>>(() =>
      apiClient.POST('/api/v5/company/{symbol}/insights/{page}/feedback/', {
        params: { path: { symbol, page } },
        body: {
          ...(vote.helpful ? { helpful: true } : { helpful: false, reason: vote.reason }),
          comment: '',
          lang: LANGUAGE,
        },
      }),
    )
  },
}
